import React, { useState, useEffect, useMemo } from 'react';
import { getRelationships, getTables } from '../services/api';

export default function RelationshipExplorer() {
    const [relationships, setRelationships] = useState([]);
    const [tables, setTables] = useState({});
    const [selectedTable, setSelectedTable] = useState(null);
    const [viewMode, setViewMode] = useState('grouped');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedTables, setExpandedTables] = useState(new Set());

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const [relRes, tablesRes] = await Promise.all([
                getRelationships(),
                getTables()
            ]);
            
            // Handle relationships
            if (relRes.data) {
                setRelationships(relRes.data.relationships || []);
                console.log(`Loaded ${relRes.data.total || 0} relationships`);
            }
            
            // Handle tables
            if (tablesRes.data && tablesRes.data.tables) {
                const tableLookup = {};
                tablesRes.data.tables.forEach(table => {
                    tableLookup[table.name] = table;
                });
                setTables(tableLookup);
            }
        } catch (err) {
            console.error('Error loading data:', err);
            setError('Failed to load relationships. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Group relationships by table
    const groupedRelationships = useMemo(() => {
        const grouped = {};
        
        relationships.forEach(rel => {
            // Initialize tables if not exists
            if (rel.from_table && !grouped[rel.from_table]) {
                grouped[rel.from_table] = { outgoing: [], incoming: [] };
            }
            if (rel.to_table && !grouped[rel.to_table]) {
                grouped[rel.to_table] = { outgoing: [], incoming: [] };
            }
            
            // Add relationships
            if (rel.from_table) {
                grouped[rel.from_table].outgoing.push(rel);
            }
            if (rel.to_table) {
                grouped[rel.to_table].incoming.push(rel);
            }
        });
        
        return grouped;
    }, [relationships]);

    // Filter tables based on search
    const filteredTables = useMemo(() => {
        return Object.keys(groupedRelationships).filter(table =>
            table.toLowerCase().includes(search.toLowerCase())
        ).sort();
    }, [groupedRelationships, search]);

    const toggleTableExpansion = (tableName) => {
        const newExpanded = new Set(expandedTables);
        if (newExpanded.has(tableName)) {
            newExpanded.delete(tableName);
        } else {
            newExpanded.add(tableName);
        }
        setExpandedTables(newExpanded);
    };

    if (loading) {
        return (
            <div className="p-8 text-center">
                <div className="inline-flex items-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                    <span>Loading relationships...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-8 text-center">
                <div className="text-red-600 mb-4">{error}</div>
                <button 
                    onClick={loadData}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    if (relationships.length === 0) {
        return (
            <div className="p-8 text-center">
                <div className="mb-4">
                    <div className="text-6xl mb-4">🔗</div>
                    <h3 className="text-lg font-semibold mb-2">No Relationships Found</h3>
                    <p className="text-gray-600">
                        Run the discovery process to analyze your database relationships.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold">Database Relationships</h2>
                    <div className="text-sm text-gray-600">
                        {relationships.length} relationships • {Object.keys(groupedRelationships).length} tables
                    </div>
                </div>
                
                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">{relationships.length}</div>
                        <div className="text-sm text-gray-600">Total Relationships</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                            {relationships.filter(r => r.source === 'database').length}
                        </div>
                        <div className="text-sm text-gray-600">From Database</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">
                            {relationships.filter(r => r.source === 'git').length}
                        </div>
                        <div className="text-sm text-gray-600">From Code Analysis</div>
                    </div>
                </div>
                
                {/* Controls */}
                <div className="flex gap-4">
                    <input
                        type="text"
                        placeholder="Search tables..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="px-4 py-2 border rounded-lg flex-1"
                    />
                    
                    <select
                        value={viewMode}
                        onChange={(e) => setViewMode(e.target.value)}
                        className="px-4 py-2 border rounded-lg"
                    >
                        <option value="grouped">Grouped by Table</option>
                        <option value="flat">All Relationships</option>
                        <option value="events">Event Mapping</option>
                    </select>
                    
                    {viewMode === 'grouped' && (
                        <button
                            onClick={() => setExpandedTables(new Set(filteredTables))}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                        >
                            Expand All
                        </button>
                    )}
                </div>
            </div>

            {/* Content based on view mode */}
            {viewMode === 'grouped' && (
                <GroupedView 
                    filteredTables={filteredTables}
                    groupedRelationships={groupedRelationships}
                    tables={tables}
                    expandedTables={expandedTables}
                    toggleTableExpansion={toggleTableExpansion}
                    setSelectedTable={setSelectedTable}
                />
            )}

            {viewMode === 'flat' && (
                <FlatView 
                    relationships={relationships}
                    search={search}
                />
            )}

            {viewMode === 'events' && (
                <EventMappingView 
                    tables={filteredTables} 
                    relationships={groupedRelationships}
                    tableInfo={tables}
                />
            )}

            {/* Modal */}
            {selectedTable && (
                <EventSuggestionModal
                    tableName={selectedTable}
                    relationships={groupedRelationships[selectedTable]}
                    tableInfo={tables[selectedTable]}
                    onClose={() => setSelectedTable(null)}
                />
            )}
        </div>
    );
}

// Grouped View Component
function GroupedView({ filteredTables, groupedRelationships, tables, expandedTables, toggleTableExpansion, setSelectedTable }) {
    if (filteredTables.length === 0) {
        return (
            <div className="text-center py-8 text-gray-500">
                No tables match your search criteria.
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredTables.map(tableName => {
                const tableRels = groupedRelationships[tableName];
                const tableInfo = tables[tableName];
                const isExpanded = expandedTables.has(tableName);
                const totalRels = tableRels.outgoing.length + tableRels.incoming.length;
                
                return (
                    <div key={tableName} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
                        <div className="p-4">
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                    <h3 className="font-semibold text-lg flex items-center">
                                        {tableName}
                                        <span className="ml-2 text-sm text-gray-500">
                                            ({totalRels} relationships)
                                        </span>
                                    </h3>
                                    {tableInfo && tableInfo.description && (
                                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                            {tableInfo.description}
                                        </p>
                                    )}
                                </div>
                                <button
                                    onClick={() => toggleTableExpansion(tableName)}
                                    className="ml-2 p-1 hover:bg-gray-100 rounded"
                                >
                                    {isExpanded ? '−' : '+'}
                                </button>
                            </div>
                            
                            {isExpanded && (
                                <div className="space-y-3">
                                    {tableRels.outgoing.length > 0 && (
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-700 mb-2">
                                                References ({tableRels.outgoing.length})
                                            </h4>
                                            <div className="space-y-1 max-h-40 overflow-y-auto">
                                                {tableRels.outgoing.map((rel, idx) => (
                                                    <RelationshipRow key={idx} rel={rel} direction="outgoing" />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {tableRels.incoming.length > 0 && (
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-700 mb-2">
                                                Referenced By ({tableRels.incoming.length})
                                            </h4>
                                            <div className="space-y-1 max-h-40 overflow-y-auto">
                                                {tableRels.incoming.map((rel, idx) => (
                                                    <RelationshipRow key={idx} rel={rel} direction="incoming" />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            <div className="mt-3 pt-3 border-t flex justify-between items-center">
                                <div className="flex gap-2">
                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                        {tableRels.outgoing.length} out
                                    </span>
                                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                        {tableRels.incoming.length} in
                                    </span>
                                </div>
                                <button
                                    onClick={() => setSelectedTable(tableName)}
                                    className="text-sm text-blue-600 hover:text-blue-800"
                                >
                                    View Events →
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// Flat View Component
function FlatView({ relationships, search }) {
    const [page, setPage] = useState(1);
    const pageSize = 50;
    
    const filteredRelationships = relationships.filter(rel =>
        (rel.from_table?.toLowerCase().includes(search.toLowerCase()) ||
         rel.to_table?.toLowerCase().includes(search.toLowerCase()) ||
         rel.from_column?.toLowerCase().includes(search.toLowerCase()) ||
         rel.to_column?.toLowerCase().includes(search.toLowerCase()))
    );
    
    const totalPages = Math.ceil(filteredRelationships.length / pageSize);
    const startIndex = (page - 1) * pageSize;
    const paginatedRelationships = filteredRelationships.slice(startIndex, startIndex + pageSize);

    return (
        <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">From Table</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">From Column</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">→</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">To Table</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">To Column</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedRelationships.map((rel, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {rel.from_table}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                {rel.from_column}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center text-gray-400">→</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {rel.to_table}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                {rel.to_column}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    rel.source === 'database' 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-blue-100 text-blue-800'
                                }`}>
                                    {rel.source || 'Unknown'}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            
            {/* Pagination */}
            {totalPages > 1 && (
                <div className="px-6 py-3 bg-gray-50 border-t flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                        Showing {startIndex + 1} to {Math.min(startIndex + pageSize, filteredRelationships.length)} of{' '}
                        {filteredRelationships.length} relationships
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setPage(Math.max(1, page - 1))}
                            disabled={page === 1}
                            className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                        >
                            Previous
                        </button>
                        <span className="px-3 py-1 text-sm">
                            Page {page} of {totalPages}
                        </span>
                        <button
                            onClick={() => setPage(Math.min(totalPages, page + 1))}
                            disabled={page === totalPages}
                            className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// Relationship Row Component
function RelationshipRow({ rel, direction }) {
    return (
        <div className="flex items-center text-sm bg-gray-50 rounded px-2 py-1 hover:bg-gray-100">
            {direction === 'outgoing' ? (
                <>
                    <span className="font-mono text-gray-700">{rel.from_column}</span>
                    <span className="mx-2 text-gray-400">→</span>
                    <span className="font-medium text-gray-900">{rel.to_table}</span>
                    <span className="text-gray-500 ml-1">({rel.to_column})</span>
                </>
            ) : (
                <>
                    <span className="font-medium text-gray-900">{rel.from_table}</span>
                    <span className="text-gray-500 mr-1">({rel.from_column})</span>
                    <span className="mx-2 text-gray-400">→</span>
                    <span className="font-mono text-gray-700">{rel.to_column}</span>
                </>
            )}
            {rel.source && (
                <span className={`ml-auto text-xs px-1.5 py-0.5 rounded ${
                    rel.source === 'database' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                }`}>
                    {rel.source}
                </span>
            )}
        </div>
    );
}

// Event Mapping View Component
function EventMappingView({ tables, relationships, tableInfo }) {
    const suggestEvents = (tableName) => {
        const events = [];
        const singular = tableName.replace(/s$/, '');
        
        // Standard CRUD events
        events.push(`${singular}.created`);
        events.push(`${singular}.updated`);
        events.push(`${singular}.deleted`);
        
        // Relationship-based events
        const rels = relationships[tableName];
        if (rels) {
            const addedEvents = new Set();
            rels.outgoing.forEach(rel => {
                if (rel.from_column.endsWith('_id')) {
                    const relEntity = rel.from_column.replace('_id', '').replace(/_/g, '-');
                    const eventName = `${singular}.${relEntity}-changed`;
                    if (!addedEvents.has(eventName)) {
                        events.push(eventName);
                        addedEvents.add(eventName);
                    }
                }
            });
        }
        
        // Special patterns based on table name
        const lowerTableName = tableName.toLowerCase();
        if (lowerTableName.includes('appointment')) {
            events.push(`${singular}.scheduled`, `${singular}.rescheduled`, `${singular}.cancelled`, `${singular}.completed`);
        }
        if (lowerTableName.includes('payment')) {
            events.push(`${singular}.processed`, `${singular}.failed`, `${singular}.refunded`);
        }
        if (lowerTableName.includes('user') || lowerTableName.includes('patient')) {
            events.push(`${singular}.registered`, `${singular}.activated`, `${singular}.deactivated`);
        }
        if (lowerTableName.includes('order')) {
            events.push(`${singular}.placed`, `${singular}.shipped`, `${singular}.delivered`);
        }
        
        return [...new Set(events)]; // Remove duplicates
    };

    const allEvents = tables.reduce((acc, tableName) => {
        const events = suggestEvents(tableName);
        events.forEach(event => {
            if (!acc[event]) {
                acc[event] = [];
            }
            acc[event].push(tableName);
        });
        return acc;
    }, {});

    return (
        <div className="space-y-6">
            {/* Guidelines */}
            <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="font-semibold mb-3">Event Type Guidelines</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <p className="text-gray-700 mb-2">
                            Events follow the pattern: <code className="bg-white px-1.5 py-0.5 rounded">entity.action</code>
                        </p>
                        <p className="text-gray-600">
                            Examples: <code className="bg-white px-1.5 py-0.5 rounded">appointment.created</code>, 
                            <code className="bg-white px-1.5 py-0.5 rounded ml-2">patient.updated</code>
                        </p>
                    </div>
                    <div>
                        <p className="text-gray-700 mb-2">Common actions:</p>
                        <p className="text-gray-600">
                            created, updated, deleted, published, archived, scheduled, completed, cancelled, failed
                        </p>
                    </div>
                </div>
            </div>
            
            {/* Event Statistics */}
            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold mb-4">Event Statistics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{Object.keys(allEvents).length}</div>
                        <div className="text-sm text-gray-600">Total Events</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{tables.length}</div>
                        <div className="text-sm text-gray-600">Tables</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">
                            {Math.round(Object.keys(allEvents).length / tables.length)}
                        </div>
                        <div className="text-sm text-gray-600">Avg Events/Table</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-orange-600">
                            {Object.keys(allEvents).filter(e => e.includes('.')).length}
                        </div>
                        <div className="text-sm text-gray-600">Custom Events</div>
                    </div>
                </div>
            </div>
            
            {/* Tables with Events */}
            <div className="grid grid-cols-1 gap-4">
                {tables.map(tableName => {
                    const events = suggestEvents(tableName);
                    const info = tableInfo[tableName];
                    
                    return (
                        <div key={tableName} className="bg-white rounded-lg shadow p-6">
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex-1">
                                    <h3 className="font-semibold text-lg">{tableName}</h3>
                                    {info?.description && (
                                        <p className="text-sm text-gray-600 mt-1">
                                            {info.description}
                                        </p>
                                    )}
                                </div>
                                <span className="ml-4 text-sm bg-gray-100 px-3 py-1 rounded-full">
                                    {events.length} events
                                </span>
                            </div>
                            
                            <div className="flex flex-wrap gap-2">
                                {events.map(event => (
                                    <span 
                                        key={event} 
                                        className="text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-blue-100 to-blue-50 text-blue-800 border border-blue-200"
                                    >
                                        {event}
                                    </span>
                                ))}
                            </div>
                            
                            {/* Relationship count */}
                            {relationships[tableName] && (
                                <div className="mt-4 pt-4 border-t text-xs text-gray-500">
                                    {relationships[tableName].outgoing.length} outgoing relationships • 
                                    {relationships[tableName].incoming.length} incoming relationships
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// Event Suggestion Modal Component
function EventSuggestionModal({ tableName, relationships, tableInfo, onClose }) {
    const suggestDetailedEvents = () => {
        const events = [];
        const singular = tableName.replace(/s$/, '');
        const seenEventNames = new Set(); // Track unique event names
        
        // Core CRUD
        const crudEvents = [
            { name: `${singular}.created`, description: `When a new ${singular} record is created` },
            { name: `${singular}.updated`, description: `When a ${singular} record is modified` },
            { name: `${singular}.deleted`, description: `When a ${singular} record is deleted` },
        ];
        
        // Add CRUD events to seen set
        crudEvents.forEach(event => seenEventNames.add(event.name));
        
        // Relationship events
        const relationshipEvents = [];
        if (relationships?.outgoing) {
            relationships.outgoing.forEach(rel => {
                if (rel.from_column.endsWith('_id')) {
                    const relEntity = rel.from_column.replace('_id', '');
                    const eventName = `${singular}.${relEntity}-changed`;
                    
                    // Only add if we haven't seen this event name before
                    if (!seenEventNames.has(eventName)) {
                        seenEventNames.add(eventName);
                        relationshipEvents.push({
                            name: eventName,
                            description: `When the ${relEntity} reference is changed`,
                            relatedTable: rel.to_table,
                            column: rel.from_column
                        });
                    }
                }
            });
        }
        
        // Status/State events
        const stateEvents = [];
        const stateEventNames = new Set();
        
        if (tableName.toLowerCase().includes('appointment')) {
            const appointmentEvents = [
                { name: `${singular}.scheduled`, description: 'When appointment is scheduled' },
                { name: `${singular}.confirmed`, description: 'When appointment is confirmed' },
                { name: `${singular}.cancelled`, description: 'When appointment is cancelled' },
                { name: `${singular}.completed`, description: 'When appointment is completed' }
            ];
            
            appointmentEvents.forEach(event => {
                if (!seenEventNames.has(event.name)) {
                    seenEventNames.add(event.name);
                    stateEvents.push(event);
                }
            });
        }
        
        if (tableName.toLowerCase().includes('payment')) {
            const paymentEvents = [
                { name: `${singular}.processed`, description: 'When payment is processed' },
                { name: `${singular}.failed`, description: 'When payment fails' },
                { name: `${singular}.refunded`, description: 'When payment is refunded' }
            ];
            
            paymentEvents.forEach(event => {
                if (!seenEventNames.has(event.name)) {
                    seenEventNames.add(event.name);
                    stateEvents.push(event);
                }
            });
        }
        
        return { crudEvents, relationshipEvents, stateEvents };
    };
    
    const { crudEvents, relationshipEvents, stateEvents } = suggestDetailedEvents();
    
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
                <div className="p-6 border-b">
                    <h3 className="text-xl font-semibold">
                        Event Suggestions: {tableName}
                    </h3>
                    {tableInfo?.description && (
                        <p className="text-sm text-gray-600 mt-1">{tableInfo.description}</p>
                    )}
                </div>
                
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                    {/* CRUD Events */}
                    <div className="mb-6">
                        <h4 className="font-medium mb-3 text-gray-900">Standard CRUD Events</h4>
                        <div className="space-y-2">
                            {crudEvents.map((event, index) => (
                                <div key={`crud-${event.name}-${index}`} className="flex items-start p-3 bg-gray-50 rounded-lg">
                                    <code className="text-sm font-mono text-blue-600 mr-3">{event.name}</code>
                                    <span className="text-sm text-gray-600">{event.description}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {/* Relationship Events */}
                    {relationshipEvents.length > 0 && (
                        <div className="mb-6">
                            <h4 className="font-medium mb-3 text-gray-900">Relationship Events</h4>
                            <div className="space-y-2">
                                {relationshipEvents.map((event, index) => (
                                    <div key={`rel-${event.name}-${index}`} className="flex items-start p-3 bg-gray-50 rounded-lg">
                                        <code className="text-sm font-mono text-purple-600 mr-3">{event.name}</code>
                                        <div className="text-sm text-gray-600">
                                            <span>{event.description}</span>
                                            {event.relatedTable && (
                                                <span className="ml-2 text-xs text-gray-500">
                                                    (references {event.relatedTable} via {event.column})
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* State Events */}
                    {stateEvents.length > 0 && (
                        <div className="mb-6">
                            <h4 className="font-medium mb-3 text-gray-900">State Transition Events</h4>
                            <div className="space-y-2">
                                {stateEvents.map((event, index) => (
                                    <div key={`state-${event.name}-${index}`} className="flex items-start p-3 bg-gray-50 rounded-lg">
                                        <code className="text-sm font-mono text-green-600 mr-3">{event.name}</code>
                                        <span className="text-sm text-gray-600">{event.description}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Webhook Example */}
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                        <h4 className="font-medium mb-2 text-gray-900">Webhook Payload Example</h4>
                        <pre className="text-xs bg-white p-3 rounded overflow-x-auto">
{JSON.stringify({
    event: `${tableName.replace(/s$/, '')}.updated`,
    timestamp: new Date().toISOString(),
    data: {
        id: "123",
        changes: {
            field: { old: "value1", new: "value2" }
        }
    },
    metadata: {
        user_id: "user_456",
        clinic_id: "clinic_789"
    }
}, null, 2)}
                        </pre>
                    </div>
                </div>
                
                <div className="p-6 border-t bg-gray-50">
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
                        >
                            Close
                        </button>
                        <button
                            onClick={() => {
                                // Copy events to clipboard
                                const allEvents = [...crudEvents, ...relationshipEvents, ...stateEvents]
                                    .map(e => e.name)
                                    .join('\n');
                                navigator.clipboard.writeText(allEvents);
                                alert('Events copied to clipboard!');
                            }}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Copy Events
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}